import { Grid, theme } from "antd";
import type { CSSProperties, ReactNode } from "react";

/** What one row shows: a title and description (like a list item's meta), free content, and trailing actions. */
export interface ItemListEntry {
  title?: ReactNode;
  description?: ReactNode;
  content?: ReactNode;
  actions?: ReactNode[];
}

interface Props<T> {
  items: readonly T[];
  itemKey(item: T): string;
  renderItem(item: T): ItemListEntry;
  size?: "small" | "default";
  /** Vertical puts the actions under the content, as on narrow screens. */
  layout?: "horizontal" | "vertical";
}

/**
 * A plain semantic list that looks like Ant Design's deprecated List (bd-15q.30): rows split by a hairline,
 * an optional title/description block and a separated row of actions.
 */
export function ItemList<T>({ items, itemKey, renderItem, size = "default", layout = "horizontal" }: Props<T>) {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const vertical = layout === "vertical";

  const itemStyle = (last: boolean): CSSProperties => ({
    // A vertical row without extra content is a plain block, as in the List it replaces.
    display: vertical ? "block" : "flex",
    flexWrap: !vertical && screens.sm === false ? "wrap" : "nowrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: size === "small" ? `${token.paddingContentVerticalSM}px ${token.paddingContentHorizontal}px` : `${token.paddingContentVertical}px 0`,
    color: token.colorText,
    borderBlockEnd: last ? "none" : `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
  });

  const meta = (entry: ItemListEntry) =>
    (entry.title !== undefined || entry.description !== undefined) && (
      <div style={{ display: "flex", flex: 1, alignItems: "flex-start", maxWidth: "100%", ...(vertical ? { marginBlockEnd: token.padding } : {}) }}>
        <div style={{ flex: "1 0", width: 0, color: token.colorText }}>
          {entry.title !== undefined && (
            <h4
              style={{
                margin: `0 0 ${vertical ? token.paddingSM : token.marginXXS}px 0`,
                color: token.colorText,
                fontSize: vertical ? token.fontSizeLG : token.fontSize,
                lineHeight: vertical ? token.lineHeightLG : token.lineHeight,
              }}
            >
              {entry.title}
            </h4>
          )}
          {entry.description !== undefined && <div style={{ color: token.colorTextDescription, fontSize: token.fontSize, lineHeight: token.lineHeight }}>{entry.description}</div>}
        </div>
      </div>
    );

  const actions = (list: ReactNode[] | undefined) =>
    list !== undefined &&
    list.length > 0 && (
      <ul
        style={{
          flex: "0 0 auto",
          margin: 0,
          // Below md the List indents actions by marginLG in both layouts; above it vertical actions start flush.
          ...(vertical ? { marginBlockStart: token.padding } : {}),
          marginInlineStart: screens.md === false ? token.marginLG : vertical ? 0 : token.marginXXL,
          padding: 0,
          fontSize: 0,
          listStyle: "none",
        }}
      >
        {list.map((action, index) => (
          <li
            // Actions are a fixed, ordered set per row.
            key={index}
            style={{
              position: "relative",
              display: "inline-block",
              padding: `0 ${vertical ? token.padding : token.paddingXS}px`,
              ...(index === 0 ? { paddingInlineStart: 0 } : {}),
              color: token.colorTextDescription,
              fontSize: token.fontSize,
              lineHeight: token.lineHeight,
              textAlign: "center",
            }}
          >
            {action}
            {index < list.length - 1 && (
              <em
                aria-hidden
                style={{
                  position: "absolute",
                  insetBlockStart: "50%",
                  insetInlineEnd: 0,
                  width: token.lineWidth,
                  height: Math.round(token.fontSize * token.lineHeight) - token.marginXXS * 2,
                  transform: "translateY(-50%)",
                  backgroundColor: token.colorSplit,
                }}
              />
            )}
          </li>
        ))}
      </ul>
    );

  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
      {items.map((item, index) => {
        const entry = renderItem(item);
        return (
          <li key={itemKey(item)} style={itemStyle(index === items.length - 1)}>
            {meta(entry)}
            {entry.content}
            {actions(entry.actions)}
          </li>
        );
      })}
    </ul>
  );
}
